/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: 2.22.0
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  AdditionalInformationUpdateRequest,
  ExternalCredits,
  ExternalError,
  ExternalInvestment,
  ExternalInvestmentCancellationRequest,
  ExternalInvestmentRequest,
  ExternalSignedDocument,
  ExternalSignedDocumentRequest,
  InvestmentDetails,
  InvestmentWalletAssociationRequest,
  InvestmentWalletSelectionRequest,
  IssuerInvestmentDetail,
  KycInformation,
  PaginatedDocumentList,
  PaginatedIdList,
  PaginatedInvestmentDetailsList,
  PatchedExternalInvestmentUpdateRequest,
  PatchedExternalSignedDocumentRequest,
} from '../models/index';
import {
    AdditionalInformationUpdateRequestFromJSON,
    AdditionalInformationUpdateRequestToJSON,
    ExternalCreditsFromJSON,
    ExternalCreditsToJSON,
    ExternalErrorFromJSON,
    ExternalErrorToJSON,
    ExternalInvestmentFromJSON,
    ExternalInvestmentToJSON,
    ExternalInvestmentCancellationRequestFromJSON,
    ExternalInvestmentCancellationRequestToJSON,
    ExternalInvestmentRequestFromJSON,
    ExternalInvestmentRequestToJSON,
    ExternalSignedDocumentFromJSON,
    ExternalSignedDocumentToJSON,
    ExternalSignedDocumentRequestFromJSON,
    ExternalSignedDocumentRequestToJSON,
    InvestmentDetailsFromJSON,
    InvestmentDetailsToJSON,
    InvestmentWalletAssociationRequestFromJSON,
    InvestmentWalletAssociationRequestToJSON,
    InvestmentWalletSelectionRequestFromJSON,
    InvestmentWalletSelectionRequestToJSON,
    IssuerInvestmentDetailFromJSON,
    IssuerInvestmentDetailToJSON,
    KycInformationFromJSON,
    KycInformationToJSON,
    PaginatedDocumentListFromJSON,
    PaginatedDocumentListToJSON,
    PaginatedIdListFromJSON,
    PaginatedIdListToJSON,
    PaginatedInvestmentDetailsListFromJSON,
    PaginatedInvestmentDetailsListToJSON,
    PatchedExternalInvestmentUpdateRequestFromJSON,
    PatchedExternalInvestmentUpdateRequestToJSON,
    PatchedExternalSignedDocumentRequestFromJSON,
    PatchedExternalSignedDocumentRequestToJSON,
} from '../models/index';

export interface ExternalDistributorV2InvestmentsCancellationCreateRequest {
    id: string;
    externalInvestmentCancellationRequest: ExternalInvestmentCancellationRequest;
}

export interface ExternalDistributorV2InvestmentsCreateRequest {
    xIdempotencyKey: string;
    externalInvestmentRequest?: ExternalInvestmentRequest;
}

export interface ExternalDistributorV2InvestmentsCreditsCreateRequest {
    id: string;
}

export interface ExternalDistributorV2InvestmentsCreditsRetrieveRequest {
    creditsId: string;
    id: string;
}

export interface ExternalDistributorV2InvestmentsListRequest {
    investorId?: string;
    limit?: number;
    offset?: number;
}

export interface ExternalDistributorV2InvestmentsPartialUpdateRequest {
    id: string;
    patchedExternalInvestmentUpdateRequest?: PatchedExternalInvestmentUpdateRequest;
}

export interface ExternalDistributorV2InvestmentsRegistryStatementsListRequest {
    id: string;
    limit?: number;
    offset?: number;
}

export interface ExternalDistributorV2InvestmentsRetrieveRequest {
    id: string;
}

export interface ExternalDistributorV2InvestmentsSignedDocumentsCreateRequest {
    xIdempotencyKey: string;
    id: string;
    externalSignedDocumentRequest: ExternalSignedDocumentRequest;
}

export interface ExternalDistributorV2InvestmentsSignedDocumentsListRequest {
    id: string;
}

export interface ExternalDistributorV2InvestmentsSignedDocumentsPartialUpdateRequest {
    id: string;
    signedDocId: string;
    patchedExternalSignedDocumentRequest?: PatchedExternalSignedDocumentRequest;
}

export interface ExternalDistributorV2InvestmentsSignedDocumentsRetrieveRequest {
    id: string;
    signedDocId: string;
}

export interface ExternalIssuerV2InvestmentsListRequest {
    limit?: number;
    offset?: number;
    productId?: string;
}

export interface ExternalIssuerV2InvestmentsRetrieveRequest {
    id: string;
}

export interface InvestmentsAbortionCreateRequest {
    id: string;
}

export interface InvestmentsAdditionalInformationCreateRequest {
    id: string;
    additionalInformationUpdateRequest?: AdditionalInformationUpdateRequest;
}

export interface InvestmentsBenefitingPersonConfirmationCreateRequest {
    id: string;
}

export interface InvestmentsIdentificationRetrieveRequest {
    id: string;
}

export interface InvestmentsIdentificationStartNotificationCreateRequest {
    id: string;
}

export interface InvestmentsListRequest {
    campaign?: string;
    distributionPlatformId?: string;
    frontendStatus?: InvestmentsListFrontendStatusEnum;
    fulfilledAmountMax?: string;
    fulfilledAmountMin?: string;
    investmentTotalFulfilledMax?: number;
    investmentTotalFulfilledMin?: number;
    investmentTotalMax?: number;
    investmentTotalMin?: number;
    investorName?: string;
    limit?: number;
    offset?: number;
    ordering?: string;
    paymentCode?: string;
    paymentTotalMax?: number;
    paymentTotalMin?: number;
    productName?: string;
    search?: string;
    signedDateAfter?: Date;
    signedDateBefore?: Date;
    status?: InvestmentsListStatusEnum;
    step?: InvestmentsListStepEnum;
    tokenId?: string;
}

export interface InvestmentsResetWalletSelectionCreateRequest {
    id: string;
}

export interface InvestmentsRetrieveRequest {
    id: string;
}

export interface InvestmentsWalletAssociationCreateRequest {
    id: string;
    investmentWalletAssociationRequest: InvestmentWalletAssociationRequest;
}

export interface InvestmentsWalletSelectionCreateRequest {
    id: string;
    investmentWalletSelectionRequest: InvestmentWalletSelectionRequest;
}

/**
 * 
 */
export class InvestmentsApi extends runtime.BaseAPI {

    /**
     * Using the cancellation endpoint, you can submit the request of an investor that the investment should be aborted. The investment will get the status ABORTED. The investment can be canceled by the investor if the investment is in one of the following states: - PENDING - KYC_PENDING - REVIEW_PENDING - WAITING_FOR_ACCEPTANCE  If the investment cannot be aborted via API an error will be returned to the client.   In addition, using the Cashlink Studio, there are other ways to cancel an investment in the issuer role. This includes the following - the KYC/AML process could not be completed in time - the payment has not been received in full and in time - the issuer has rejected the investment  These issuer cancellations cannot be submitted via the API, but only via the Cashlink Studio.
     * Cancel an investment
     */
    async externalDistributorV2InvestmentsCancellationCreateRaw(requestParameters: ExternalDistributorV2InvestmentsCancellationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalInvestment>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsCancellationCreate().'
            );
        }

        if (requestParameters['externalInvestmentCancellationRequest'] == null) {
            throw new runtime.RequiredError(
                'externalInvestmentCancellationRequest',
                'Required parameter "externalInvestmentCancellationRequest" was null or undefined when calling externalDistributorV2InvestmentsCancellationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/cancellation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: ExternalInvestmentCancellationRequestToJSON(requestParameters['externalInvestmentCancellationRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalInvestmentFromJSON(jsonValue));
    }

    /**
     * Using the cancellation endpoint, you can submit the request of an investor that the investment should be aborted. The investment will get the status ABORTED. The investment can be canceled by the investor if the investment is in one of the following states: - PENDING - KYC_PENDING - REVIEW_PENDING - WAITING_FOR_ACCEPTANCE  If the investment cannot be aborted via API an error will be returned to the client.   In addition, using the Cashlink Studio, there are other ways to cancel an investment in the issuer role. This includes the following - the KYC/AML process could not be completed in time - the payment has not been received in full and in time - the issuer has rejected the investment  These issuer cancellations cannot be submitted via the API, but only via the Cashlink Studio.
     * Cancel an investment
     */
    async externalDistributorV2InvestmentsCancellationCreate(requestParameters: ExternalDistributorV2InvestmentsCancellationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalInvestment> {
        const response = await this.externalDistributorV2InvestmentsCancellationCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * This endpoint can be used to create an investment either by directly importing an investment (Distributor API product offering) or by processing an invitation into an investment (Investment Platform API product offering).  ### Importing an investment (Distributor API product offering) For directly creating investments you need the following data: - The ID of the investor the investment is created for - The ID of the campaign - The amount the investor is about to invest - Date and time when the investor has signed the investment contract - Set of signed documents  In addition, you may add signed documents for the investment via the signed documents endpoint.  All investments need to be accepted by the issuer before they can be processed further. This can either be done via the Cashlink Studio or programmatically via the API.  If you provide the accepted_at field in the request to create an investment, the issuer doesn\'t need to perform this action via the Cashlink Studio. If you omit the field the issuer is notified via email that a new investment can be accepted. Note that you\'re not able to update the investment with the accepted_at attribute after creation.  ### Processing an invitation into an investment (Investment Platform API product offering)  If all needed data has been set for the self-disclosure and knowledge and experiences, the invitation can be used to create an investment by providing the following data: - ID of the invitation - Date and time when the investor has signed the investment contract  Investments involve documents that legally describe the investment contract with the issuer (such as a subscription form). Make sure that your investors are displayed these investment documents before investing and that they accept each document using a checkbox, for example.
     * Create an investment
     */
    async externalDistributorV2InvestmentsCreateRaw(requestParameters: ExternalDistributorV2InvestmentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalInvestment>> {
        if (requestParameters['xIdempotencyKey'] == null) {
            throw new runtime.RequiredError(
                'xIdempotencyKey',
                'Required parameter "xIdempotencyKey" was null or undefined when calling externalDistributorV2InvestmentsCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (requestParameters['xIdempotencyKey'] != null) {
            headerParameters['X-Idempotency-Key'] = String(requestParameters['xIdempotencyKey']);
        }

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: ExternalInvestmentRequestToJSON(requestParameters['externalInvestmentRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalInvestmentFromJSON(jsonValue));
    }

    /**
     * This endpoint can be used to create an investment either by directly importing an investment (Distributor API product offering) or by processing an invitation into an investment (Investment Platform API product offering).  ### Importing an investment (Distributor API product offering) For directly creating investments you need the following data: - The ID of the investor the investment is created for - The ID of the campaign - The amount the investor is about to invest - Date and time when the investor has signed the investment contract - Set of signed documents  In addition, you may add signed documents for the investment via the signed documents endpoint.  All investments need to be accepted by the issuer before they can be processed further. This can either be done via the Cashlink Studio or programmatically via the API.  If you provide the accepted_at field in the request to create an investment, the issuer doesn\'t need to perform this action via the Cashlink Studio. If you omit the field the issuer is notified via email that a new investment can be accepted. Note that you\'re not able to update the investment with the accepted_at attribute after creation.  ### Processing an invitation into an investment (Investment Platform API product offering)  If all needed data has been set for the self-disclosure and knowledge and experiences, the invitation can be used to create an investment by providing the following data: - ID of the invitation - Date and time when the investor has signed the investment contract  Investments involve documents that legally describe the investment contract with the issuer (such as a subscription form). Make sure that your investors are displayed these investment documents before investing and that they accept each document using a checkbox, for example.
     * Create an investment
     */
    async externalDistributorV2InvestmentsCreate(requestParameters: ExternalDistributorV2InvestmentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalInvestment> {
        const response = await this.externalDistributorV2InvestmentsCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * For tokenized products only. You are able to trigger the credit of digital securities by providing the ID of the investment. The credit can only be triggered if a wallet has been created for the investor and assigned to the investment. Also, the payment amount must has been marked as received.  The credit triggers a minting transaction on-ledger that increases the balance of the wallet associated with the investment with the purchased number of units. The response contains a status field with the following codes - processing: The task is created and is currently being executed - failed: The task could not be completed - succeeded: The task was completed successfully - unknown: The task needs manual investigation to determine if the transaction has actually succeeded.  If the task failed because the minting transaction on-ledger could not be executed the error message should indicate that. If the task has succeeded the status of the investment is updated to DELIVERED.  If there is a credit initiated for an investment, it’s not possible to create a new credit for that investment, instead an error is returned to the client.
     * Credit digital securities for an investment
     */
    async externalDistributorV2InvestmentsCreditsCreateRaw(requestParameters: ExternalDistributorV2InvestmentsCreditsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalCredits>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsCreditsCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/credits/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalCreditsFromJSON(jsonValue));
    }

    /**
     * For tokenized products only. You are able to trigger the credit of digital securities by providing the ID of the investment. The credit can only be triggered if a wallet has been created for the investor and assigned to the investment. Also, the payment amount must has been marked as received.  The credit triggers a minting transaction on-ledger that increases the balance of the wallet associated with the investment with the purchased number of units. The response contains a status field with the following codes - processing: The task is created and is currently being executed - failed: The task could not be completed - succeeded: The task was completed successfully - unknown: The task needs manual investigation to determine if the transaction has actually succeeded.  If the task failed because the minting transaction on-ledger could not be executed the error message should indicate that. If the task has succeeded the status of the investment is updated to DELIVERED.  If there is a credit initiated for an investment, it’s not possible to create a new credit for that investment, instead an error is returned to the client.
     * Credit digital securities for an investment
     */
    async externalDistributorV2InvestmentsCreditsCreate(requestParameters: ExternalDistributorV2InvestmentsCreditsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalCredits> {
        const response = await this.externalDistributorV2InvestmentsCreditsCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Retrieve information for a triggered crediting
     * Retrieve a credit of an investment
     */
    async externalDistributorV2InvestmentsCreditsRetrieveRaw(requestParameters: ExternalDistributorV2InvestmentsCreditsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalCredits>> {
        if (requestParameters['creditsId'] == null) {
            throw new runtime.RequiredError(
                'creditsId',
                'Required parameter "creditsId" was null or undefined when calling externalDistributorV2InvestmentsCreditsRetrieve().'
            );
        }

        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsCreditsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:read"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/credits/{credits_id}/`.replace(`{${"credits_id"}}`, encodeURIComponent(String(requestParameters['creditsId']))).replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalCreditsFromJSON(jsonValue));
    }

    /**
     * Retrieve information for a triggered crediting
     * Retrieve a credit of an investment
     */
    async externalDistributorV2InvestmentsCreditsRetrieve(requestParameters: ExternalDistributorV2InvestmentsCreditsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalCredits> {
        const response = await this.externalDistributorV2InvestmentsCreditsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Use this endpoint to get a list of all investments.
     * List investments
     */
    async externalDistributorV2InvestmentsListRaw(requestParameters: ExternalDistributorV2InvestmentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedIdList>> {
        const queryParameters: any = {};

        if (requestParameters['investorId'] != null) {
            queryParameters['investor_id'] = requestParameters['investorId'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:read"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedIdListFromJSON(jsonValue));
    }

    /**
     * Use this endpoint to get a list of all investments.
     * List investments
     */
    async externalDistributorV2InvestmentsList(requestParameters: ExternalDistributorV2InvestmentsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedIdList> {
        const response = await this.externalDistributorV2InvestmentsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * You can update an existing investment via this endpoint.  **Add payment date to record a payment for an investment**  Payments of investors for investments can be recorded via the Cashlink Studio. You can -  in addition or exclusively - mark investments as paid via the API by adding a payment date to the investment via the investments endpoint after the investment has been accepted by the issuer.  The payment amount is calculated based on the investment amount, agio, disagio and accrued interest. The investment amount and disagio are controlled via the API. The agio and accrued interest is calculated based on the product configuration.  **Add the wallet id to provide settlement information**  For an investment to settle, it needs the wallet owned by the investor that should be used for this particular investment. Wallets can be associated with an investment directly during creation or they can be added after the initial creation by updating the investment.  The wallet association can be updated as long as the issuance transaction has not been triggered for that investment.
     * Update an investment
     */
    async externalDistributorV2InvestmentsPartialUpdateRaw(requestParameters: ExternalDistributorV2InvestmentsPartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalInvestment>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsPartialUpdate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'PATCH',
            headers: headerParameters,
            query: queryParameters,
            body: PatchedExternalInvestmentUpdateRequestToJSON(requestParameters['patchedExternalInvestmentUpdateRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalInvestmentFromJSON(jsonValue));
    }

    /**
     * You can update an existing investment via this endpoint.  **Add payment date to record a payment for an investment**  Payments of investors for investments can be recorded via the Cashlink Studio. You can -  in addition or exclusively - mark investments as paid via the API by adding a payment date to the investment via the investments endpoint after the investment has been accepted by the issuer.  The payment amount is calculated based on the investment amount, agio, disagio and accrued interest. The investment amount and disagio are controlled via the API. The agio and accrued interest is calculated based on the product configuration.  **Add the wallet id to provide settlement information**  For an investment to settle, it needs the wallet owned by the investor that should be used for this particular investment. Wallets can be associated with an investment directly during creation or they can be added after the initial creation by updating the investment.  The wallet association can be updated as long as the issuance transaction has not been triggered for that investment.
     * Update an investment
     */
    async externalDistributorV2InvestmentsPartialUpdate(requestParameters: ExternalDistributorV2InvestmentsPartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalInvestment> {
        const response = await this.externalDistributorV2InvestmentsPartialUpdateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List all EWpG documents of the given investment.
     * List all related registry statements
     */
    async externalDistributorV2InvestmentsRegistryStatementsListRaw(requestParameters: ExternalDistributorV2InvestmentsRegistryStatementsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedDocumentList>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsRegistryStatementsList().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:read"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/registry-statements/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedDocumentListFromJSON(jsonValue));
    }

    /**
     * List all EWpG documents of the given investment.
     * List all related registry statements
     */
    async externalDistributorV2InvestmentsRegistryStatementsList(requestParameters: ExternalDistributorV2InvestmentsRegistryStatementsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedDocumentList> {
        const response = await this.externalDistributorV2InvestmentsRegistryStatementsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Retrieve investment information via ID. The response will contain a status field with the following values: | Status      | Description   | | ----------- | ------------- | | KYC_PENDING | The investment is signed but identification process for the investor is still pending.| | REVIEW_PENDING | The external review process is still pending. | | WAITING_FOR_ACCEPTANCE | The investment is ready to be accepted by the issuer. | | ACCEPTED | The investment has been accepted by the issuer. | | PAID | The investment amount has been marked as paid. | | DELIVERED | The digital securities have been credited to the recipient\'s wallet. | | KYC_INSUFFICIENT | The KYC/AML check could not be completed successfully due to missing or inconsistent data. | | ABORTION | The order has been canceled by the investor. | | REJECTION | Order execution has been rejected by the issuer. | | CANCELLATION | The contract has been canceled by the investor during the cancellation period. | | PAYMENT_MISSING | The order has been canceled because the payment has not yet arrived on time. |
     * Retrieve an investment
     */
    async externalDistributorV2InvestmentsRetrieveRaw(requestParameters: ExternalDistributorV2InvestmentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalInvestment>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:read"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalInvestmentFromJSON(jsonValue));
    }

    /**
     * Retrieve investment information via ID. The response will contain a status field with the following values: | Status      | Description   | | ----------- | ------------- | | KYC_PENDING | The investment is signed but identification process for the investor is still pending.| | REVIEW_PENDING | The external review process is still pending. | | WAITING_FOR_ACCEPTANCE | The investment is ready to be accepted by the issuer. | | ACCEPTED | The investment has been accepted by the issuer. | | PAID | The investment amount has been marked as paid. | | DELIVERED | The digital securities have been credited to the recipient\'s wallet. | | KYC_INSUFFICIENT | The KYC/AML check could not be completed successfully due to missing or inconsistent data. | | ABORTION | The order has been canceled by the investor. | | REJECTION | Order execution has been rejected by the issuer. | | CANCELLATION | The contract has been canceled by the investor during the cancellation period. | | PAYMENT_MISSING | The order has been canceled because the payment has not yet arrived on time. |
     * Retrieve an investment
     */
    async externalDistributorV2InvestmentsRetrieve(requestParameters: ExternalDistributorV2InvestmentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalInvestment> {
        const response = await this.externalDistributorV2InvestmentsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * If you\'ve directly created an investment via the investments endpoints you can upload signed documents and add them to the investment via this endpoint. Adding signed documents to an investment is not possible if you\'ve used an invitation to create the investment. In this case, all signed documents are created automatically.
     * Add a new signed document for an investment
     */
    async externalDistributorV2InvestmentsSignedDocumentsCreateRaw(requestParameters: ExternalDistributorV2InvestmentsSignedDocumentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalSignedDocument>> {
        if (requestParameters['xIdempotencyKey'] == null) {
            throw new runtime.RequiredError(
                'xIdempotencyKey',
                'Required parameter "xIdempotencyKey" was null or undefined when calling externalDistributorV2InvestmentsSignedDocumentsCreate().'
            );
        }

        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsSignedDocumentsCreate().'
            );
        }

        if (requestParameters['externalSignedDocumentRequest'] == null) {
            throw new runtime.RequiredError(
                'externalSignedDocumentRequest',
                'Required parameter "externalSignedDocumentRequest" was null or undefined when calling externalDistributorV2InvestmentsSignedDocumentsCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (requestParameters['xIdempotencyKey'] != null) {
            headerParameters['X-Idempotency-Key'] = String(requestParameters['xIdempotencyKey']);
        }

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/signed-documents/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: ExternalSignedDocumentRequestToJSON(requestParameters['externalSignedDocumentRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalSignedDocumentFromJSON(jsonValue));
    }

    /**
     * If you\'ve directly created an investment via the investments endpoints you can upload signed documents and add them to the investment via this endpoint. Adding signed documents to an investment is not possible if you\'ve used an invitation to create the investment. In this case, all signed documents are created automatically.
     * Add a new signed document for an investment
     */
    async externalDistributorV2InvestmentsSignedDocumentsCreate(requestParameters: ExternalDistributorV2InvestmentsSignedDocumentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalSignedDocument> {
        const response = await this.externalDistributorV2InvestmentsSignedDocumentsCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * This endpoint can be used to retrieve all signed documents for an investment. If you\'ve processed an invitation into an investment, the signed documents are generated after the KYC/AML process for that investor has been finished successfully.
     * List signed documents for an investment
     */
    async externalDistributorV2InvestmentsSignedDocumentsListRaw(requestParameters: ExternalDistributorV2InvestmentsSignedDocumentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<ExternalSignedDocument>>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsSignedDocumentsList().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:read"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/signed-documents/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(ExternalSignedDocumentFromJSON));
    }

    /**
     * This endpoint can be used to retrieve all signed documents for an investment. If you\'ve processed an invitation into an investment, the signed documents are generated after the KYC/AML process for that investor has been finished successfully.
     * List signed documents for an investment
     */
    async externalDistributorV2InvestmentsSignedDocumentsList(requestParameters: ExternalDistributorV2InvestmentsSignedDocumentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<ExternalSignedDocument>> {
        const response = await this.externalDistributorV2InvestmentsSignedDocumentsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Update the specified signed document.
     * Update a signed document for an investment
     */
    async externalDistributorV2InvestmentsSignedDocumentsPartialUpdateRaw(requestParameters: ExternalDistributorV2InvestmentsSignedDocumentsPartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalSignedDocument>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsSignedDocumentsPartialUpdate().'
            );
        }

        if (requestParameters['signedDocId'] == null) {
            throw new runtime.RequiredError(
                'signedDocId',
                'Required parameter "signedDocId" was null or undefined when calling externalDistributorV2InvestmentsSignedDocumentsPartialUpdate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/signed-documents/{signed_doc_id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))).replace(`{${"signed_doc_id"}}`, encodeURIComponent(String(requestParameters['signedDocId']))),
            method: 'PATCH',
            headers: headerParameters,
            query: queryParameters,
            body: PatchedExternalSignedDocumentRequestToJSON(requestParameters['patchedExternalSignedDocumentRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalSignedDocumentFromJSON(jsonValue));
    }

    /**
     * Update the specified signed document.
     * Update a signed document for an investment
     */
    async externalDistributorV2InvestmentsSignedDocumentsPartialUpdate(requestParameters: ExternalDistributorV2InvestmentsSignedDocumentsPartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalSignedDocument> {
        const response = await this.externalDistributorV2InvestmentsSignedDocumentsPartialUpdateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Retrieve a single signed document instance for given investment.
     * Retrieve a signed document of an investment
     */
    async externalDistributorV2InvestmentsSignedDocumentsRetrieveRaw(requestParameters: ExternalDistributorV2InvestmentsSignedDocumentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalSignedDocument>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvestmentsSignedDocumentsRetrieve().'
            );
        }

        if (requestParameters['signedDocId'] == null) {
            throw new runtime.RequiredError(
                'signedDocId',
                'Required parameter "signedDocId" was null or undefined when calling externalDistributorV2InvestmentsSignedDocumentsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:read"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/investments/{id}/signed-documents/{signed_doc_id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))).replace(`{${"signed_doc_id"}}`, encodeURIComponent(String(requestParameters['signedDocId']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalSignedDocumentFromJSON(jsonValue));
    }

    /**
     * Retrieve a single signed document instance for given investment.
     * Retrieve a signed document of an investment
     */
    async externalDistributorV2InvestmentsSignedDocumentsRetrieve(requestParameters: ExternalDistributorV2InvestmentsSignedDocumentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalSignedDocument> {
        const response = await this.externalDistributorV2InvestmentsSignedDocumentsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Use this endpoint to get a list of all investments.
     * List all investments
     */
    async externalIssuerV2InvestmentsListRaw(requestParameters: ExternalIssuerV2InvestmentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedIdList>> {
        const queryParameters: any = {};

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['productId'] != null) {
            queryParameters['product_id'] = requestParameters['productId'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:read"]);
        }

        const response = await this.request({
            path: `/api/external/issuer/v2/investments/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedIdListFromJSON(jsonValue));
    }

    /**
     * Use this endpoint to get a list of all investments.
     * List all investments
     */
    async externalIssuerV2InvestmentsList(requestParameters: ExternalIssuerV2InvestmentsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedIdList> {
        const response = await this.externalIssuerV2InvestmentsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Show the details of an investment.
     * Show investment details
     */
    async externalIssuerV2InvestmentsRetrieveRaw(requestParameters: ExternalIssuerV2InvestmentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<IssuerInvestmentDetail>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalIssuerV2InvestmentsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:read"]);
        }

        const response = await this.request({
            path: `/api/external/issuer/v2/investments/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => IssuerInvestmentDetailFromJSON(jsonValue));
    }

    /**
     * Show the details of an investment.
     * Show investment details
     */
    async externalIssuerV2InvestmentsRetrieve(requestParameters: ExternalIssuerV2InvestmentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<IssuerInvestmentDetail> {
        const response = await this.externalIssuerV2InvestmentsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsAbortionCreateRaw(requestParameters: InvestmentsAbortionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsAbortionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/{id}/abortion/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsAbortionCreate(requestParameters: InvestmentsAbortionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsAbortionCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsAdditionalInformationCreateRaw(requestParameters: InvestmentsAdditionalInformationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsAdditionalInformationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/{id}/additional-information/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: AdditionalInformationUpdateRequestToJSON(requestParameters['additionalInformationUpdateRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsAdditionalInformationCreate(requestParameters: InvestmentsAdditionalInformationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsAdditionalInformationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsBenefitingPersonConfirmationCreateRaw(requestParameters: InvestmentsBenefitingPersonConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsBenefitingPersonConfirmationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/{id}/benefiting-person-confirmation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsBenefitingPersonConfirmationCreate(requestParameters: InvestmentsBenefitingPersonConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsBenefitingPersonConfirmationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Retrieve an identification. Create it if it does not exists.
     */
    async investmentsIdentificationRetrieveRaw(requestParameters: InvestmentsIdentificationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<KycInformation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsIdentificationRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/{id}/identification/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => KycInformationFromJSON(jsonValue));
    }

    /**
     * Retrieve an identification. Create it if it does not exists.
     */
    async investmentsIdentificationRetrieve(requestParameters: InvestmentsIdentificationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<KycInformation> {
        const response = await this.investmentsIdentificationRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Set the KYC status of an identification to \'started\'.
     */
    async investmentsIdentificationStartNotificationCreateRaw(requestParameters: InvestmentsIdentificationStartNotificationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsIdentificationStartNotificationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/{id}/identification-start-notification/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Set the KYC status of an identification to \'started\'.
     */
    async investmentsIdentificationStartNotificationCreate(requestParameters: InvestmentsIdentificationStartNotificationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsIdentificationStartNotificationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsListRaw(requestParameters: InvestmentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedInvestmentDetailsList>> {
        const queryParameters: any = {};

        if (requestParameters['campaign'] != null) {
            queryParameters['campaign'] = requestParameters['campaign'];
        }

        if (requestParameters['distributionPlatformId'] != null) {
            queryParameters['distribution_platform_id'] = requestParameters['distributionPlatformId'];
        }

        if (requestParameters['frontendStatus'] != null) {
            queryParameters['frontend_status'] = requestParameters['frontendStatus'];
        }

        if (requestParameters['fulfilledAmountMax'] != null) {
            queryParameters['fulfilled_amount_max'] = requestParameters['fulfilledAmountMax'];
        }

        if (requestParameters['fulfilledAmountMin'] != null) {
            queryParameters['fulfilled_amount_min'] = requestParameters['fulfilledAmountMin'];
        }

        if (requestParameters['investmentTotalFulfilledMax'] != null) {
            queryParameters['investment_total_fulfilled_max'] = requestParameters['investmentTotalFulfilledMax'];
        }

        if (requestParameters['investmentTotalFulfilledMin'] != null) {
            queryParameters['investment_total_fulfilled_min'] = requestParameters['investmentTotalFulfilledMin'];
        }

        if (requestParameters['investmentTotalMax'] != null) {
            queryParameters['investment_total_max'] = requestParameters['investmentTotalMax'];
        }

        if (requestParameters['investmentTotalMin'] != null) {
            queryParameters['investment_total_min'] = requestParameters['investmentTotalMin'];
        }

        if (requestParameters['investorName'] != null) {
            queryParameters['investor_name'] = requestParameters['investorName'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['paymentCode'] != null) {
            queryParameters['payment_code'] = requestParameters['paymentCode'];
        }

        if (requestParameters['paymentTotalMax'] != null) {
            queryParameters['payment_total_max'] = requestParameters['paymentTotalMax'];
        }

        if (requestParameters['paymentTotalMin'] != null) {
            queryParameters['payment_total_min'] = requestParameters['paymentTotalMin'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['search'] != null) {
            queryParameters['search'] = requestParameters['search'];
        }

        if (requestParameters['signedDateAfter'] != null) {
            queryParameters['signed_date_after'] = (requestParameters['signedDateAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['signedDateBefore'] != null) {
            queryParameters['signed_date_before'] = (requestParameters['signedDateBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['status'] != null) {
            queryParameters['status'] = requestParameters['status'];
        }

        if (requestParameters['step'] != null) {
            queryParameters['step'] = requestParameters['step'];
        }

        if (requestParameters['tokenId'] != null) {
            queryParameters['token_id'] = requestParameters['tokenId'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedInvestmentDetailsListFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsList(requestParameters: InvestmentsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedInvestmentDetailsList> {
        const response = await this.investmentsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * If before setting up a wallet, an investor decides to choose another wallet type, then this endpoint needs to be called to reset the `add_wallet_later` field.
     */
    async investmentsResetWalletSelectionCreateRaw(requestParameters: InvestmentsResetWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsResetWalletSelectionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/{id}/reset-wallet-selection/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * If before setting up a wallet, an investor decides to choose another wallet type, then this endpoint needs to be called to reset the `add_wallet_later` field.
     */
    async investmentsResetWalletSelectionCreate(requestParameters: InvestmentsResetWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsResetWalletSelectionCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsRetrieveRaw(requestParameters: InvestmentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<InvestmentDetails>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestmentDetailsFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsRetrieve(requestParameters: InvestmentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<InvestmentDetails> {
        const response = await this.investmentsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsWalletAssociationCreateRaw(requestParameters: InvestmentsWalletAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsWalletAssociationCreate().'
            );
        }

        if (requestParameters['investmentWalletAssociationRequest'] == null) {
            throw new runtime.RequiredError(
                'investmentWalletAssociationRequest',
                'Required parameter "investmentWalletAssociationRequest" was null or undefined when calling investmentsWalletAssociationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/{id}/wallet-association/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestmentWalletAssociationRequestToJSON(requestParameters['investmentWalletAssociationRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsWalletAssociationCreate(requestParameters: InvestmentsWalletAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsWalletAssociationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Select a wallet type without creating a wallet immediately. Used for mobile wallet onboarding.
     */
    async investmentsWalletSelectionCreateRaw(requestParameters: InvestmentsWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsWalletSelectionCreate().'
            );
        }

        if (requestParameters['investmentWalletSelectionRequest'] == null) {
            throw new runtime.RequiredError(
                'investmentWalletSelectionRequest',
                'Required parameter "investmentWalletSelectionRequest" was null or undefined when calling investmentsWalletSelectionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/investments/{id}/wallet-selection/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestmentWalletSelectionRequestToJSON(requestParameters['investmentWalletSelectionRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Select a wallet type without creating a wallet immediately. Used for mobile wallet onboarding.
     */
    async investmentsWalletSelectionCreate(requestParameters: InvestmentsWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsWalletSelectionCreateRaw(requestParameters, initOverrides);
    }

}

/**
 * @export
 */
export const InvestmentsListFrontendStatusEnum = {
    ABORTED: 'Aborted',
    ACCEPTANCE_MISSING: 'AcceptanceMissing',
    ADDITIONAL_INFORMATION_PENDING: 'AdditionalInformationPending',
    CANCELED_KYC_INSUFFICIENT: 'CanceledKycInsufficient',
    CANCELED_PAYMENT_MISSING: 'CanceledPaymentMissing',
    CANCELLATION: 'Cancellation',
    FINISHED: 'Finished',
    HANDOVER: 'Handover',
    IDENTIFICATION_COMPLIANCE_PENDING: 'IdentificationCompliancePending',
    IDENTIFICATION_PENDING: 'IdentificationPending',
    PAYMENT_PENDING: 'PaymentPending',
    REJECTED: 'Rejected',
    SECURITIES_TRANSFERRED: 'SecuritiesTransferred',
    SECURITY_TRANSFER_MISSING: 'SecurityTransferMissing',
    SUCCESS_FULLY_TRANSFERED: 'SuccessFullyTransfered',
    SUCCESS_PARTIALLY_TRANSFERED: 'SuccessPartiallyTransfered',
    SUCCESS_REDEEMED: 'SuccessRedeemed',
    TAX_INFORMATION_MISSING: 'TaxInformationMissing',
    TRANSACTION_FAILED: 'TransactionFailed',
    TRANSACTION_PENDING: 'TransactionPending',
    TRANSACTION_SUCCEEDED: 'TransactionSucceeded',
    WALLET_SETUP_PENDING: 'WalletSetupPending'
} as const;
export type InvestmentsListFrontendStatusEnum = typeof InvestmentsListFrontendStatusEnum[keyof typeof InvestmentsListFrontendStatusEnum];
/**
 * @export
 */
export const InvestmentsListStatusEnum = {
    ACCEPTABLE: 'acceptable',
    ALL: 'all',
    FINISHED: 'finished',
    NOT_DELETED: 'not-deleted',
    OPEN: 'open',
    PAID: 'paid',
    PAYMENT_OUTSTANDING: 'payment-outstanding',
    SECURITIES_OUTSTANDING: 'securities-outstanding'
} as const;
export type InvestmentsListStatusEnum = typeof InvestmentsListStatusEnum[keyof typeof InvestmentsListStatusEnum];
/**
 * @export
 */
export const InvestmentsListStepEnum = {
    ACCOUNT_SETUP: 'ACCOUNT_SETUP',
    ADDITIONAL_INFORMATION: 'ADDITIONAL_INFORMATION',
    BENEFICIARY: 'BENEFICIARY',
    CANCELED: 'CANCELED',
    CONFIRM_WALLET: 'CONFIRM_WALLET',
    FINAL: 'FINAL',
    IDENTIFICATION: 'IDENTIFICATION',
    IDENTIFICATION_FAILED: 'IDENTIFICATION_FAILED',
    IDENTIFICATION_REJECTED: 'IDENTIFICATION_REJECTED',
    INVESTMENT_AMOUNT: 'INVESTMENT_AMOUNT',
    OFFER_CREATION: 'OFFER_CREATION',
    PAYMENT: 'PAYMENT',
    PREPARE_ASSET: 'PREPARE_ASSET',
    QUESTIONNAIRE: 'QUESTIONNAIRE',
    RESUME_IDENTIFICATION: 'RESUME_IDENTIFICATION',
    SELECT_WALLET: 'SELECT_WALLET',
    SETUP_MOBILE_WALLET: 'SETUP_MOBILE_WALLET',
    TAX_INFORMATION: 'TAX_INFORMATION',
    UNCONFIRMED: 'UNCONFIRMED',
    VIB: 'VIB',
    WAITING_ACCEPTANCE: 'WAITING_ACCEPTANCE',
    WAITING_COMPLIANCE: 'WAITING_COMPLIANCE',
    WAITING_KYC: 'WAITING_KYC'
} as const;
export type InvestmentsListStepEnum = typeof InvestmentsListStepEnum[keyof typeof InvestmentsListStepEnum];
